import { useState, useEffect, useCallback } from "react";
import { Course as BaseCourse, Module } from "../../types/course.types";
import type { CourseMenuItem } from "@/types/course.types";
import { ApiTopic, ApiTopicUpdate, ApiTopicCreate } from "../../types/courseContentApi.types";
import { Course as ApiCourse } from "../../../../../../types/course.types";
import { CourseFormApiService, CourseApi } from "../../../../../../services/course.service";
import { TopicApiService } from "../../../../../../services/course-content.service";
import { UploadApi } from "../../../../../../services/upload.service";
import { SeminarActivity } from "@/types/seminar.types";

import { tr } from '@/i18n';
export interface Course extends BaseCourse {
  tags: string[];
  instructors: any[];
  duration_minutes: number | null;
  cover_image_url: string | null;
  category?: { id: number; name: string } | null;
  custom_field_values?: any[];
  image_items?: any[];
  menu_config?: CourseMenuItem[] | null;
  intro_video_url?: string | null;
}

const mapApiDataToCourse = (
  apiCourse: ApiCourse,
  apiTopics: ApiTopic[]
): Course => {
  return {
    id: apiCourse.id,
    fullname: apiCourse.fullname,
    summary: apiCourse.summary || "",
    modules: apiTopics
      .sort((a, b) => a.order - b.order)
      .map((topic) => ({
        id: topic.id.toString(),
        icon: topic.image_url || null,
        title: topic.title,
        blocks: [],
        type: topic.completion_type === 'seminar' ? 'seminar' : 'lesson',
        activities: [...(topic.activities || [])].sort((a, b) => a.order - b.order),
      })),
    certificate: apiCourse.certificate_template_id
      ? {
          templateId: apiCourse.certificate_template_id,
          title: apiCourse.certificate_template?.name || tr('course.certificate', 'Certificate'),
        }
      : null,
    tags: apiCourse.tags || [],
    instructors: apiCourse.instructors || [],
    duration_minutes: apiCourse.duration_minutes || null,
    cover_image_url: (apiCourse as any).cover_image_url || null,
    category: (apiCourse as any).category || null,
    custom_field_values: (apiCourse as any).custom_field_values || [],
    image_items: (apiCourse as any).image_items || [],
    menu_config: apiCourse.menu_config ?? null,
    intro_video_url: apiCourse.intro_video_url ?? null,
  };
};

export const useCourseBuilder = (courseId?: string) => {
  const [course, setCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastDeletedModule, setLastDeletedModule] = useState<{
    module: Module;
    index: number;
    timerId: NodeJS.Timeout;
    moduleId: string;
  } | null>(null);

  const fetchCourseData = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const numId = parseInt(id, 10);
      if (isNaN(numId)) throw new Error(tr("ui.invalid_course_id", "Invalid Course ID"));

      // Fetch course details directly by ID (not from catalog search)
      const courseData = await CourseApi.getCourseById(numId);

      const courseTopics = await TopicApiService.listTopicsForCourse(numId);
      setCourse(mapApiDataToCourse(courseData, courseTopics));
    } catch (err) {
      console.error("Failed to fetch course data:", err);
      setError(tr("ui.failed_to_load_course_data_please_try_again", "Failed to load course data. Please try again."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (courseId) {
      fetchCourseData(courseId);
    } else {
      setIsLoading(false);
      setError(tr("ui.no_course_id_provided", "No course ID provided."));
    }
  }, [courseId, fetchCourseData]);

  const updateCourseState = (updater: (prevCourse: Course) => Course) => {
    setCourse((prev) => (prev ? updater(prev) : null));
  };

  const reorderModules = async (reorderedModules: Module[]) => {
    if (!course) return;
    updateCourseState((prev) => ({ ...prev, modules: reorderedModules }));
    try {
      const updatePromises = reorderedModules.map((module, index) =>
        TopicApiService.updateTopic(parseInt(module.id, 10), { order: index + 1 })
      );
      await Promise.all(updatePromises);
    } catch (err) {
      console.error("Failed to reorder modules:", err);
      setError(tr("ui.failed_to_save_new_module_order", "Failed to save new module order."));
      if (course?.id) fetchCourseData(String(course.id));
    }
  };

  const addModule = async (title: string) => {
    if (!course) return;
    try {
      const payload: ApiTopicCreate = {
        title,
        course_id: course.id,
        summary: "",
        image_url: "📚",
        order: course.modules.length + 1,
        is_visible: true,
        completion_type: 'manual',
      };
      await TopicApiService.createTopic(payload);
      await fetchCourseData(String(course.id));
    } catch (err) {
      console.error("Failed to add module:", err);
      setError(tr("ui.could_not_add_the_module", "Could not add the module."));
    }
  };

  const addSeminarModule = async (seminarData: SeminarActivity) => {
    if (!course) return;
    try {
      const payload: ApiTopicCreate = {
        title: seminarData.name,
        course_id: course.id,
        summary: seminarData.description || "",
        image_url: "🗓️",
        order: course.modules.length + 1,
        is_visible: true,
        completion_type: 'seminar',
        activity_id: seminarData.id,
      };
      await TopicApiService.createTopic(payload);
      await fetchCourseData(String(course.id));
    } catch (err) {
      console.error("Failed to add seminar module:", err);
      setError(tr("ui.could_not_add_the_seminar_as_a_module", "Could not add the seminar as a module."));
    }
  };

  const updateModuleProperty = async <K extends keyof ApiTopicUpdate>(
    moduleId: string,
    property: K,
    value: ApiTopicUpdate[K]
  ) => {
    if (!course) return;
    updateCourseState((prev) => ({
      ...prev,
      modules: prev.modules.map((mod) => {
        if (mod.id !== moduleId) return mod;
        const updatedModule = { ...mod };
        if (property === "title") updatedModule.title = value as string;
        if (property === "image_url") updatedModule.icon = value as string | null;
        return updatedModule;
      }),
    }));
    try {
      await TopicApiService.updateTopic(parseInt(moduleId, 10), { [property]: value });
    } catch (err) {
      console.error(`Failed to update module ${property}:`, err);
      setError(tr("ui.failed_to_save_module_x_reverting_changes", "Failed to save module {v0}. Reverting changes.", { v0: property }));
      if (course?.id) fetchCourseData(String(course.id));
    }
  };

  const deleteModule = (moduleId: string): Module | null => {
    if (!course) return null;

    const moduleToDelete = course.modules.find((m) => m.id === moduleId);
    const originalIndex = course.modules.findIndex((m) => m.id === moduleId);
    if (!moduleToDelete || originalIndex === -1) return null;

    if (lastDeletedModule) {
      clearTimeout(lastDeletedModule.timerId);
      TopicApiService.deleteTopic(parseInt(lastDeletedModule.moduleId, 10)).catch((err) => {
        console.error("Failed to delete previous module:", err);
      });
    }

    updateCourseState((prev) => ({
      ...prev,
      modules: prev.modules.filter((m) => m.id !== moduleId),
    }));

    const timerId = setTimeout(() => {
      TopicApiService.deleteTopic(parseInt(moduleId, 10)).catch((err) => {
        console.error("Failed to delete module permanently:", err);
        setError(tr("ui.could_not_delete_the_module", "Could not delete the module."));
        if (course?.id) fetchCourseData(String(course.id));
      });
      setLastDeletedModule(null);
    }, 5000);

    setLastDeletedModule({ module: moduleToDelete, index: originalIndex, timerId, moduleId });
    return moduleToDelete;
  };

  const undoDeleteModule = () => {
    if (!lastDeletedModule || !course) return;
    clearTimeout(lastDeletedModule.timerId);
    const restoredModules = [...course.modules];
    restoredModules.splice(lastDeletedModule.index, 0, lastDeletedModule.module);
    updateCourseState((prev) => ({ ...prev, modules: restoredModules }));
    setLastDeletedModule(null);
  };

  const duplicateModule = async (moduleId: string) => {
    if (!course) return;
    const moduleToDuplicate = course.modules.find((m) => m.id === moduleId);
    const originalIndex = course.modules.findIndex((m) => m.id === moduleId);
    if (!moduleToDuplicate) return;
    try {
      await TopicApiService.createTopic({
        title: tr('ui.x_copy', '{v0} (Copy)', { v0: moduleToDuplicate.title }),
        summary: "",
        image_url: moduleToDuplicate.icon || "📚",
        order: originalIndex + 2,
        is_visible: true,
        course_id: course.id,
      });
      await fetchCourseData(String(course.id));
    } catch (err) {
      console.error("Failed to duplicate module:", err);
      setError(tr("ui.could_not_duplicate_the_module", "Could not duplicate the module."));
    }
  };

  const assignCertificate = async (templateId: number) => {
    if (!course) return;
    try {
      await CourseFormApiService.updateItem(course.id, {
        certificate_template_id: templateId,
        certificate_issue_on_completion: true,
      });
      await fetchCourseData(String(course.id));
    } catch (err) {
      console.error("Failed to assign certificate:", err);
      setError(tr("ui.could_not_assign_the_certificate", "Could not assign the certificate."));
    }
  };

  const removeCertificate = async () => {
    if (!course) return;
    try {
      await CourseFormApiService.updateItem(course.id, {
        certificate_template_id: null,
        certificate_issue_on_completion: false,
      });
      await fetchCourseData(String(course.id));
    } catch (err) {
      console.error("Failed to remove certificate:", err);
      setError(tr("ui.could_not_remove_the_certificate", "Could not remove the certificate."));
    }
  };

  const updateCourseTags = async (tags: string[]) => {
    if (!course) return;
    updateCourseState((prev) => ({ ...prev, tags }));
    try {
      await CourseFormApiService.updateItem(course.id, { tags });
    } catch (err) {
      console.error("Failed to update tags:", err);
      setError(tr("ui.failed_to_save_topics_tags", "Failed to save topics (tags)."));
    }
  };

  const updateCourseInstructor = async (instructorData: any, photoFile?: File) => {
    if (!course) return;
    let overridePhotoId = null;
    if (photoFile) {
      try {
        const uploadedFile = await UploadApi.uploadFile(photoFile, 'instructors');
        overridePhotoId = uploadedFile.id;
      } catch (err) {
        console.error("Failed to upload instructor photo:", err);
        setError(tr("ui.failed_to_upload_instructor_photo", "Failed to upload instructor photo."));
        return;
      }
    }
    const instructorsPayload = [{
      user_id: instructorData.userId || 1,
      override_photo_id: overridePhotoId,
      override_bio: instructorData.bio
    }];
    try {
      await CourseFormApiService.updateItem(course.id, { instructors: instructorsPayload });
      updateCourseState((prev) => ({
        ...prev,
        instructors: [{
          user_id: instructorData.userId || 1,
          name: instructorData.name,
          bio: instructorData.bio,
          photo_url: photoFile ? URL.createObjectURL(photoFile) : instructorData.avatarUrl,
          position_name: instructorData.role
        }]
      }));
    } catch (err) {
      console.error("Failed to update instructor:", err);
      setError(tr("ui.could_not_save_instructor_details", "Could not save instructor details."));
    }
  };

  const removeCourseInstructor = async () => {
    if (!course) return;
    try {
      await CourseFormApiService.updateItem(course.id, { instructors: [] });
      updateCourseState((prev) => ({ ...prev, instructors: [] }));
    } catch (err) {
      console.error("Failed to remove instructor:", err);
      setError(tr("ui.could_not_remove_instructor", "Could not remove instructor."));
    }
  };

  const refetch = useCallback(() => {
    if (courseId) return fetchCourseData(courseId);
  }, [courseId, fetchCourseData]);

  return {
    course,
    isLoading,
    error,
    setCourse,
    refetch,
    reorderModules,
    addModule,
    addSeminarModule,
    updateModuleProperty,
    deleteModule,
    undoDeleteModule,
    duplicateModule,
    assignCertificate,
    removeCertificate,
    updateCourseTags,
    updateCourseInstructor,
    removeCourseInstructor
  };
};